import { REQUEST_TIMEOUT } from "../../constants";

export interface GithubRepositoryStats {
  stars: number;
  forks: number;
  watchers: number;
}

function parseGithubRepository(repoUrl: string) {
  try {
    const url = new URL(repoUrl);
    const isSupportedProtocol =
      url.protocol === "http:" || url.protocol === "https:";
    if (!isSupportedProtocol || url.hostname !== "github.com") {
      return undefined;
    }

    const pathParts = url.pathname.replace(/^\/+|\/+$/g, "").split("/");
    if (pathParts.length !== 2) {
      return undefined;
    }

    const [owner, rawRepo] = pathParts;
    const repo = rawRepo.replace(/\.git$/, "");
    return owner && repo ? { owner, repo } : undefined;
  } catch {
    return undefined;
  }
}

function isGithubRepositoryResponse(value: unknown): value is {
  stargazers_count: number;
  forks_count: number;
  watchers_count: number;
} {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const data = value as Record<string, unknown>;
  return (
    typeof data.stargazers_count === "number" &&
    typeof data.forks_count === "number" &&
    typeof data.watchers_count === "number"
  );
}

export async function getGithubRepositoryStats(
  repoUrl: string,
): Promise<GithubRepositoryStats> {
  const repository = parseGithubRepository(repoUrl);
  if (!repository) {
    throw new Error("Invalid GitHub repository URL");
  }

  const response = await fetch(
    `https://api.github.com/repos/${encodeURIComponent(repository.owner)}/${encodeURIComponent(repository.repo)}`,
    {
      headers: {
        Accept: "application/vnd.github+json",
        "X-GitHub-Api-Version": "2022-11-28",
      },
      signal: AbortSignal.timeout(REQUEST_TIMEOUT),
    },
  );

  if (!response.ok) {
    throw new Error(`GitHub API returned status ${response.status}`);
  }

  const body: unknown = await response.json().catch(() => undefined);
  if (!isGithubRepositoryResponse(body)) {
    throw new Error("Invalid GitHub API response");
  }

  return {
    stars: body.stargazers_count,
    forks: body.forks_count,
    watchers: body.watchers_count,
  };
}
